// custom date/time related utils
// move things from filters-helpers.js to here

import { 
    DateTime,
    Duration,
} from "luxon";


// DATE UTILS
// -----------------------------------------------------------------------

const parseDateFromStringToLuxon = (text) => {
    // parse a string of text AS A DATE (not datetime) to a Luxon DateTime object - must be a string, otherwise throws error
    // uses variety of methods and THROWS ERROR if it can't parse
    // caller should catch this and return some specific default
    // TODO - add locale options for this... but the different Luxon methods take different forms of locale options...
    const valueType = typeof text;
    if (valueType !== 'string') {
        throw new Error(`parseDateFromStringToLuxon requires a string, but received ${valueType}`);
    }
    let dt = DateTime.fromISO(text);
    if (dt.isValid) {
        console.log('parsed from ISO');
        return dt;
    }
    dt = DateTime.fromRFC2822(text);
    if (dt.isValid) {
        console.log('parsed from RFC2822');
        return dt;
    }
    dt = DateTime.fromHTTP(text);
    if (dt.isValid) {
        console.log('parsed from HTTP');
        return dt;
    }
    // try fromFormat with some common formats, it will depend on user locale.... so we use the built-in shortcuts for date formats
    // we WANT these to be locale-based, so we use DateTime.parseFormatForOpts() to get the right format for the user's locale
    const shortDate = DateTime.parseFormatForOpts(DateTime.DATE_SHORT);
    const medDate = DateTime.parseFormatForOpts(DateTime.DATE_MED);
    const fullDate = DateTime.parseFormatForOpts(DateTime.DATE_FULL);
    const hugeDate = DateTime.parseFormatForOpts(DateTime.DATE_HUGE);
    console.log('locale-based date formats are: ', shortDate, medDate, fullDate, hugeDate);
    // also make a 2-digit year version of the short date (or all of them???) - just in case user enters 2-digit year
    const twoDigitShortDate = shortDate.replace(/[y]{4,6}/g, "yy");  // replace 4-digit year with 2-digit year -- the actual format strings have 4-6 y's
    const twoDigitMedDate = medDate.replace(/[y]{4,6}/g, "yy");
    const twoDigitFullDate = fullDate.replace(/[y]{4,6}/g, "yy");
    const twoDigitHugeDate = hugeDate.replace(/[y]{4,6}/g, "yy");
    console.log('locale-based date formats after replacement: ', shortDate, medDate, fullDate, hugeDate, twoDigitShortDate, twoDigitMedDate, twoDigitFullDate, twoDigitHugeDate);
    const commonFormats = [
        shortDate,
        medDate,
        fullDate,
        hugeDate,
        twoDigitShortDate,
        twoDigitMedDate,
        twoDigitFullDate,
        twoDigitHugeDate,
    ];

    for (let [idx, fmt] of commonFormats.entries()) {
        dt = DateTime.fromFormat(text, fmt);
        if (dt.isValid) {
            console.log(`parsed from format: ${fmt}, (index ${idx})`);
            return dt;
        }
    }
    // if we get here, we couldn't parse it
    console.warn(`Could not parse date string: ${text}`);

    throw new Error(`Could not parse date string: ${text}`);  // throw error so caller can handle it
}

const toLuxonDate = (value) => {
    // the SUPER PARSER! just for DATES though... not full date times
    const defaultReturn = null;
    if (!value) {
        // if value is anything falsey, just return null - so this won't support 0 duration
        return defaultReturn;
    }
    if (typeof value === 'string') {
        try {
            return parseDateFromStringToLuxon(value);
        } catch (error) {
            console.warn(`Failed to parse date from string: ${value}`, error);
            return defaultReturn;
        }
    } else if (value instanceof Date) {
        const jsDate = DateTime.fromJSDate(value);
        return jsDate.isValid ? jsDate : defaultReturn;
    } else if (typeof value === 'number') {
        const dateFromMillis = DateTime.fromMillis(value);
        return dateFromMillis.isValid ? dateFromMillis : defaultReturn;
    } else if (typeof value === 'object') {
        // object could be native DateTime object already, or object with { year: 2023, month: 10, day: 1 }
        if (DateTime.isDateTime(value)) {
            return value;
        }
        const dateFromObj = DateTime.fromObject(value);
        return dateFromObj && dateFromObj.isValid ? dateFromObj : defaultReturn;
    }
    return defaultReturn;  // the final default 
}




// DURATION UTILS
// -----------------------------------------------------------------------

const parseDurationFromText = (text) => {
    // this is crude... but can handle basic things like '6 days', '5 weeks'
    // can parse out a single number and either hour,hours,day,days,week,weeks,month,months,year,years
    // we allow hours/days/weeks/months/years because that's what our dropdown supports in QuoteSheet reply right now
    // returns a luxon Duration instance 

    const regex = /(\d+) (hour|hours|day|days|week|weeks|month|months|year|years)/;

    // Alternative syntax using RegExp constructor
    // const regex = new RegExp('(\\d+) (day|days|week|weeks|month|months)', '')

    const str = text;   // `8 weeks`;
    let m;
    const returnDefault = null;
    let luxonDuration = returnDefault;  // what we return

    // group 0 will be the combined string captured ('8 week')
    // group 1 will be the number ('8') as a string
    // group 2 will be day, days, week, weeks, month, months ('week') - no 's' version gets captured first if first in list

    m = regex.exec(str);
    if (m !== null && m.length === 3) {
        // The result can be accessed through the `m`-variable.
        // m is not an array, but has .length and is inherited from Array
        // m.forEach((match, groupIndex) => {
        //     console.log(`Found match, group ${groupIndex}: ${match}`);
        // });
        // console.log(m);
        const qtyString = m[1];
        const qty = Number.parseInt(qtyString);
        const periodRaw = m[2];
        // make an object you can pass to Duration.fromObject()
        // keys can be years, quarters, months, weeks, days, hours, minutes, seconds, milliseconds (all with 's')
        const period = (!!periodRaw && periodRaw.endsWith('s')) ? periodRaw : `${periodRaw}s`;
        const durObj = {};
        durObj[period] = qty;
        const luxDur = Duration.fromObject(durObj);
        luxonDuration = luxDur.isValid ? luxDur : null;
    } else {
        // console.log('no valid regex capture');
    }
    
    return luxonDuration;
}

const toLuxonDuration = (value) => {
    const defaultReturn = null;
    if (!value) {
        // if value is anything falsey, just return null - so this won't support 0 duration
        return defaultReturn;
    }
    if (typeof value === 'string') {
        const durFromIso = Duration.fromISO(value);
        if (durFromIso && durFromIso.isValid) {
            return durFromIso;
        }
        // next try regex on this string
        const durFromRegex = parseDurationFromText(value);
        if (durFromRegex && durFromRegex.isValid) {
            return durFromRegex;
        }
        // neither worked?  just return the default
        return defaultReturn;
    } else if (typeof value === 'number') {
        const dur = Duration.fromMillis(value);
        return dur.isValid ? dur : defaultReturn;
    } else if (typeof value === 'object') {
        // object could be native Duration object already, or object with { weeks: 8, ... }
        if (Duration.isDuration(value)) {
            return value;
        }
        const durFromObj = Duration.fromObject(value);
        if (durFromObj && durFromObj.isValid) {
            return durFromObj;
        }
        return defaultReturn;
    }
    return defaultReturn;  // the final default 
}


export {
    parseDateFromStringToLuxon,
    toLuxonDate,
    toLuxonDuration,
    parseDurationFromText,
}
